import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, Input, Select, Space, Table, Tag } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ArrowLeftOutlined, CloudUploadOutlined, PlayCircleOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import MwsEnvironmentSelector from '../components/MwsEnvironmentSelector';
import ScriptExecutionService from '../http/SslScriptService';
import { DEPLOY_ENV } from '../utils/config';
import { ScriptExecution, ScriptExecutionCommandType, ScriptExecutionRunResult, ScriptExecutionServer } from '../entities/SslScript';
import './SslScriptPage.css';

const SCRIPT_NAME = 'ssl-auto-renewal';
const COMMAND_HINTS: Record<string, string> = {
    reconcile: '推荐日常使用：按环境对证书和 nginx 配置做对齐同步。',
    sync: '只同步指定域名，请补充 --domain=xxx。',
    cdn: '用于 CDN 场景同步证书，请补充 --domain=xxx。',
    reload: '仅执行 nginx reload，不做额外同步。',
};

const SslScriptPage = (): React.JSX.Element => {
    document.title = 'SSL更新脚本';
    const navigate = useNavigate();
    const [script, setScript] = useState<ScriptExecution | null>(null);
    const [scriptPath, setScriptPath] = useState('/usr/local/bin/ssl-auto-renewal.sh');
    const [scriptVersion, setScriptVersion] = useState(1);
    const [content, setContent] = useState('');
    const [servers, setServers] = useState<ScriptExecutionServer[]>([]);
    const [selectedIps, setSelectedIps] = useState<string[]>([]);
    const [allServers, setAllServers] = useState(true);
    const [commandType, setCommandType] = useState<ScriptExecutionCommandType>('SCRIPT');
    const [command, setCommand] = useState(`reconcile --env=${DEPLOY_ENV}`);
    const [deployBeforeRun, setDeployBeforeRun] = useState(false);
    const [results, setResults] = useState<ScriptExecutionRunResult[]>([]);
    const [loading, setLoading] = useState(false);

    const selectedCount = allServers ? servers.length : selectedIps.length;
    const commandKeyword = command.trim().split(/\s+/)[0]?.toLowerCase();
    const commandHint = COMMAND_HINTS[commandKeyword] || '可填写脚本参数，例如：reconcile --env=test 或 sync --env=test --domain=example.com';

    const commandPresets = useMemo(() => [
        { label: 'reconcile', value: `reconcile --env=${DEPLOY_ENV}` },
        { label: 'sync', value: `sync --env=${DEPLOY_ENV} --domain=` },
        { label: 'cdn', value: `cdn --env=${DEPLOY_ENV} --domain=` },
        { label: 'reload', value: `reload --env=${DEPLOY_ENV}` },
    ], []);

    useEffect(() => {
        refresh();
    }, []);

    const refresh = async () => {
        setLoading(true);
        try {
            const [scriptData, serverData] = await Promise.all([
                ScriptExecutionService.getScript({ environment: DEPLOY_ENV, scriptName: SCRIPT_NAME }),
                ScriptExecutionService.listServers({ environment: DEPLOY_ENV, scriptName: SCRIPT_NAME }),
            ]);
            if (scriptData) {
                setScript(scriptData);
                setScriptPath(scriptData.scriptPath || '/usr/local/bin/ssl-auto-renewal.sh');
                setScriptVersion(scriptData.scriptVersion || 1);
                setContent(scriptData.content || '');
            }
            setServers(serverData);
            setSelectedIps(serverData.map((server) => server.internalIp));
        } finally {
            setLoading(false);
        }
    };

    const buildTargetRequest = () => ({
        environment: DEPLOY_ENV,
        scriptName: SCRIPT_NAME,
        allServers,
        serverIps: allServers ? [] : selectedIps,
    });

    const ensureTarget = () => {
        if (selectedCount === 0) {
            toast.error('请选择服务器');
            return false;
        }
        return true;
    };

    const saveScript = async () => {
        setLoading(true);
        try {
            if (!script?.id) {
                toast.error('脚本不存在');
                return;
            }
            const response = await ScriptExecutionService.updateScript({ id: script.id, scriptPath, content, environment: DEPLOY_ENV, scriptName: SCRIPT_NAME });
            if (response.isFailed()) {
                return;
            }
            setScript(response.data);
            toast.success('脚本已保存');
        } finally {
            setLoading(false);
        }
    };

    const deployScript = async () => {
        if (!ensureTarget()) {
            return;
        }
        setLoading(true);
        try {
            const data = await ScriptExecutionService.deployScript(buildTargetRequest());
            setResults(data);
        } finally {
            setLoading(false);
        }
    };

    const runScript = async () => {
        if (!ensureTarget()) {
            return;
        }
        setLoading(true);
        try {
            const data = await ScriptExecutionService.runScript({
                ...buildTargetRequest(),
                commandType,
                command,
                deployBeforeRun,
            });
            setResults(data);
        } finally {
            setLoading(false);
        }
    };

    const serverColumns = [
        {
            title: '服务器',
            dataIndex: 'instanceName',
            render: (value: string, record: ScriptExecutionServer) => value || record.internalIp,
        },
        {
            title: '内网IP',
            dataIndex: 'internalIp',
            width: 150,
        },
        {
            title: '公网IP',
            dataIndex: 'externalIp',
            width: 150,
        },
        {
            title: '状态',
            dataIndex: 'state',
            width: 90,
            render: (value: string) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag>,
        },
    ];

    const resultColumns = [
        {
            title: '服务器',
            dataIndex: 'internalIp',
            width: 170,
            render: (value: string, record: ScriptExecutionRunResult) => (
                <div>
                    <div>{record.instanceName || value}</div>
                    <div className="ssl-script-muted">{value}</div>
                </div>
            ),
        },
        {
            title: '命令',
            dataIndex: 'command',
            ellipsis: true,
        },
        {
            title: '版本',
            dataIndex: 'scriptVersion',
            width: 110,
            render: (value: number) => value || '-',
        },
        {
            title: '退出码',
            dataIndex: 'exitCode',
            width: 90,
            render: (value: number) => <Tag color={value === 0 ? 'green' : 'red'}>{value}</Tag>,
        },
    ];

    return (
        <div className="ssl-script-page">
            <div className="ssl-script-header">
                <div className="ssl-script-title">
                    <Button type="link" className="ssl-script-back" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} />
                    <span>SSL更新脚本</span>
                    <MwsEnvironmentSelector prefix="nginx" enableDevHost={true} />
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>刷新</Button>
                    <Button icon={<SaveOutlined />} type="primary" onClick={saveScript} loading={loading}>保存</Button>
                    <Button icon={<CloudUploadOutlined />} onClick={deployScript} loading={loading}>部署</Button>
                    <Button icon={<PlayCircleOutlined />} type="primary" danger={commandType === 'CERTBOT_RENEW'} onClick={runScript} loading={loading}>运行</Button>
                </Space>
            </div>

            <div className="ssl-script-meta">
                <Input addonBefore="路径" value={scriptPath} onChange={(event) => setScriptPath(event.target.value)} />
                <div className="ssl-script-version-box">
                    <span className="ssl-script-version-label">版本</span>
                    <Tag color="blue">v{scriptVersion}</Tag>
                </div>
                <div className="ssl-script-updated">更新时间：{script?.updateTime || '-'}</div>
            </div>

            <div className="ssl-script-workspace">
                <section className="ssl-script-editor">
                    <TextArea value={content} onChange={(event) => setContent(event.target.value)} spellCheck={false} />
                </section>

                <aside className="ssl-script-side">
                    <section className="ssl-script-panel">
                        <div className="ssl-script-panel-header">
                            <span>目标服务器</span>
                            <Tag>{selectedCount}</Tag>
                        </div>
                        <Checkbox checked={allServers} onChange={(event) => setAllServers(event.target.checked)}>全量运行</Checkbox>
                        <div className="ssl-script-muted">
                            全量同步方式：保持“全量运行”勾选，然后点击上方“部署”。会将脚本同步到当前环境所有 ACTIVE nginx 服务器。
                        </div>
                        <Table<ScriptExecutionServer>
                            className="ssl-script-server-table"
                            rowKey="internalIp"
                            size="small"
                            columns={serverColumns}
                            dataSource={servers}
                            pagination={false}
                            loading={loading}
                            rowSelection={allServers ? undefined : {
                                selectedRowKeys: selectedIps,
                                onChange: (keys) => setSelectedIps(keys.map(String)),
                            }}
                        />
                    </section>

                    <section className="ssl-script-panel">
                        <div className="ssl-script-panel-header">
                            <span>运行命令</span>
                        </div>
                        <Select<ScriptExecutionCommandType>
                            value={commandType}
                            onChange={setCommandType}
                            options={[
                                { label: '脚本命令', value: 'SCRIPT' },
                                { label: 'certbot renew', value: 'CERTBOT_RENEW' },
                            ]}
                        />
                        {commandType === 'SCRIPT' && (
                            <>
                                <Select value={command} onChange={setCommand} options={commandPresets} />
                                <Input
                                    value={command}
                                    onChange={(event) => setCommand(event.target.value)}
                                    placeholder="例如：reconcile --env=test"
                                />
                            </>
                        )}
                        <div className="ssl-script-command-help">
                            {commandType === 'CERTBOT_RENEW' ? (
                                <>
                                    <div className="ssl-script-command-help-title">命令提示</div>
                                    <div className="ssl-script-muted">该模式会自动组装 certbot renew 命令，你无需手输参数。</div>
                                </>
                            ) : (
                                <>
                                    <div className="ssl-script-command-help-title">命令提示</div>
                                    <div className="ssl-script-muted">{commandHint}</div>
                                    <div className="ssl-script-muted">示例：reconcile --env={DEPLOY_ENV}</div>
                                    <div className="ssl-script-muted">示例：sync --env={DEPLOY_ENV} --domain=demo.com</div>
                                </>
                            )}
                        </div>
                        <Checkbox checked={deployBeforeRun} onChange={(event) => setDeployBeforeRun(event.target.checked)}>运行前部署脚本</Checkbox>
                    </section>
                </aside>
            </div>

            <section className="ssl-script-results">
                <div className="ssl-script-panel-header">
                    <span>运行结果</span>
                    <Tag>{results.length}</Tag>
                </div>
                <Table<ScriptExecutionRunResult>
                    rowKey={(record) => `${record.internalIp}-${record.command}-${record.exitCode}`}
                    size="small"
                    columns={resultColumns}
                    dataSource={results}
                    pagination={false}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div className="ssl-script-output-grid">
                                <div>
                                    <div className="ssl-script-output-title">stdout</div>
                                    <pre>{record.output || '-'}</pre>
                                </div>
                                <div>
                                    <div className="ssl-script-output-title">stderr</div>
                                    <pre>{record.error || '-'}</pre>
                                </div>
                            </div>
                        ),
                    }}
                />
            </section>
        </div>
    );
};

export default SslScriptPage;
